import React, { useState, useEffect, useRef } from 'react';
import './AudioRecorder.css';

function AudioRecorder({ isRecording, setIsRecording, onTranscription, disabled }) {
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
      setRecordingTime(0);
    }

    return () => clearInterval(timerRef.current);
  }, [isRecording]);

  const startRecording = async () => {
    try {
      // Request audio with better constraints for speech recognition
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000  // 16kHz is good for speech recognition
        }
      });

      // Check supported MIME types
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 128000  // 128 kbps for better quality
      });
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        // Small delay to ensure the blob is fully finalized
        setTimeout(() => {
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
          console.log(`Audio recording complete: ${audioBlob.size} bytes, MIME type: ${mimeType}`);
          onTranscription(audioBlob);

          // Stop all audio tracks
          stream.getTracks().forEach((track) => track.stop());
        }, 100); // 100ms delay to ensure audio data is complete
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      console.log('Recording started with MIME type:', mimeType);
    } catch (error) {
      console.error('Error accessing microphone:', error);

      // Provide helpful guidance based on the error
      let errorMessage = 'Impossible d\'accéder au microphone.\n\n';
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        errorMessage += 'Autorisation refusée. Veuillez:\n\n';
        errorMessage += '1. Cliquer sur l\'icône 🔒 dans la barre d\'adresse\n';
        errorMessage += '2. Autoriser l\'accès au microphone\n';
        errorMessage += '3. Rafraîchir la page\n\n';
        errorMessage += 'Conseil: Utilisez un micro de bonne qualité et réduisez le bruit ambiant.\n\n';
        errorMessage += 'Ou dans les paramètres du navigateur:\n';
        errorMessage += '- Chrome: chrome://settings/content/microphone\n';
        errorMessage += '- Firefox: about:preferences#privacy → Permissions → Microphone';
      } else if (error.name === 'NotFoundError') {
        errorMessage += 'Aucun microphone détecté.\n\n';
        errorMessage += 'Vérifiez qu\'un microphone est bien connecté à votre ordinateur.';
      } else {
        errorMessage += 'Erreur: ' + error.message + '\n\n';
        errorMessage += 'Vérifiez que votre navigateur autorise l\'accès au microphone pour localhost.';
      }

      alert(errorMessage);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      // Note: The onstop handler will be called when recording is fully stopped
      // and the audio blob will be ready after a brief delay
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <button
      onClick={isRecording ? stopRecording : startRecording}
      disabled={disabled}
      className={`record-btn ${isRecording ? 'recording' : ''}`}
      title={isRecording ? 'Arrêter l\'enregistrement' : 'Enregistrer'}
    >
      {isRecording ? (
        <>
          <span className="record-icon">&#9632;</span>
          <span className="record-time">{formatTime(recordingTime)}</span>
        </>
      ) : (
        <span className="record-icon">&#128265;</span>
      )}
    </button>
  );
}

export default AudioRecorder;
